import { nanoid } from "nanoid";
import { joinLines, mapTabs } from "../../helpers/exportHelpers";
import { IExportArtifact } from "../ExportArtifacts/IExportArtifact";
import { IModelInfo } from "../ModelInfos/IModelInfo";

export function generateCsharpDataMapping(models: IModelInfo[]): IExportArtifact[] {
    const artifacts: IExportArtifact[] = [];
    const tab = "    ";
    const line = "\r\n";

    // Model(s).Model
    // Model(s).ModelMap
    // Model(s).ModelRepository

    for (const model of models) {
        const pluralName = `${model.name}s`
        const namespace = `DataMapping.${pluralName}`;

        artifacts.push(getModelDmClassArtifact(model, tab, line, namespace, `DataMapping/${pluralName}/Dm${model.name}.cs`));
        artifacts.push(getModelDmExtensionsArtifact(model, tab, line, namespace, `DataMapping/${pluralName}/${model.name}Extensions.cs`));
        artifacts.push(getModelDmMapArtifact(model, tab, line, namespace, `DataMapping/${pluralName}/Dm${model.name}Map.cs`));
        artifacts.push(getModelRepositoryArtifact(model, tab, line, namespace, `DataMapping/${pluralName}/${model.name}Repository.cs`));
    }

    return artifacts;
}

function getModelDmClassArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const properties = model.members.map(x => `\tpublic ${x.type.name} ${x.name} { get; set; }`);
    const className = `${model.name}`;
    const classContent = mapTabs(tab, [
        `public class ${className}`,
        `{`,
        ...properties,
        `}`
    ], 1);

    const imports = ["System", "System.Threading.Tasks", "System.Collections.Generic", ...model.imports];
    const usings = imports.map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}

function getModelDmExtensionsArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const copyProperties = model.members.map(x => `\tdest.${x.name} = src.${x.name};`);
    const className = `${model.name}`;
    const targetClass = `Dm${model.name}`;

    const toDomainMapper = mapTabs(tab, [
        `public static ${className} ToDomain(this ${targetClass} src, ${className} dest = null)`,
        `{`,
        `\tif (src == null) return null;`,
        `\tdest ??= new ${className}();`,
        ...copyProperties,
        `\treturn dest;`,
        `}`
        ], 1);
    const fromDomainMapper = mapTabs(tab, [
            `public static ${targetClass} FromDomain(this ${className} src, ${targetClass} dest = null)`,
            `{`,
            `\tif (src == null) return null;`,
            `\tdest ??= new ${targetClass}();`,
            ...copyProperties,
            `\treturn dest;`,
            `}`
            ], 1);

    const extClass = `${model.name}Extensions`;
    const classContent = mapTabs(tab, [
        `public static class ${extClass}`,
        `{`,
        ...toDomainMapper,
        ...fromDomainMapper,
        `}`
    ], 1);

    const imports = ["System", "System.Threading.Tasks", "System.Collections.Generic", ...model.imports];
    const usings = imports.map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}


function getModelDmMapArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const className = `Dm${model.name}`;
    const entityConfigure = mapTabs(tab, [
        `public void Configure(EntityTypeBuilder<${className}> entityTypeBuilder)`,
        `{`,
            `${tab}entityTypeBuilder.ToTable("${model.originalName}");`,
            `${tab}entityTypeBuilder.HasKey(x => x.${model.members[0].name});`,
            ...model.members.map(p => `${tab}entityTypeBuilder.Property(x => x.${p.name}).HasColumnName("${p.originalName}");`),
        `}`
        ], 1);

    const classContent = mapTabs(tab, [
        `public class ${className}Map : IEntityTypeConfiguration<${className}>`,
        `{`,
        ...entityConfigure,
        `}`
    ], 1);

    const usings = model.imports.map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}

function getModelRepositoryArtifact(model: IModelInfo, tab: string, line: string, namespace: string, fileName: string) {
    const properties = [
        `${tab}public Task<IEnumerable<${model.name}>> GetAll()`,
        "{",
        "",
        "}"
    ];

    const classContent = mapTabs(tab, [
        `public class ${model.name}Repository`,
        `{`,
        ...properties,
        `}`
    ], 1);

    const allUsings = new Set<string>([...model.imports, "System", "System.Threading", "System.Collections.Generic"]);
    const usings = Array.from(allUsings).map(x => `using ${x};`);

    const fileContent = joinLines(line, [
        ...usings,
        '',
        `namespace ${namespace}`,
        `{`,
        ...classContent,
        `}`,
        ''
    ]);

    const artifact: IExportArtifact = {
        id: nanoid(),
        exportId: nanoid(),
        created: new Date(),
        fileName: fileName,
        content: fileContent
    };
    return artifact;
}